import * as React from 'react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  Paper,
  Grid,
  IconButton,
  Tooltip,
  Link,
  CircularProgress,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DescriptionIcon from '@mui/icons-material/Description';
import ReceiptIcon from '@mui/icons-material/Receipt';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import ScreenshotIcon from '@mui/icons-material/Screenshot';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import EmailIcon from '@mui/icons-material/Email';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ExtensionIcon from '@mui/icons-material/Extension';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import LinkIcon from '@mui/icons-material/Link';
import InventoryIcon from '@mui/icons-material/Inventory';
import BuildIcon from '@mui/icons-material/Build';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BusinessIcon from '@mui/icons-material/Business';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PendingIcon from '@mui/icons-material/Pending';
import { withTranslation } from 'react-i18next';
import axios from 'axios';

// Document type icons
const documentTypeConfig = {
  lasku: { icon: DescriptionIcon, color: 'primary', label: 'Lasku' },
  kuitti: { icon: ReceiptIcon, color: 'success', label: 'Kuitti' },
  lahete: { icon: LocalShippingIcon, color: 'info', label: 'Lähete' },
  hinnasto: { icon: AttachMoneyIcon, color: 'warning', label: 'Hinnasto' },
  tuoteluettelo: { icon: MenuBookIcon, color: 'secondary', label: 'Tuoteluettelo' },
  tarjous: { icon: RequestQuoteIcon, color: 'info', label: 'Tarjous' },
  kuvakaappaus: { icon: ScreenshotIcon, color: 'default', label: 'Kuvakaappaus' },
  muu: { icon: HelpOutlineIcon, color: 'default', label: 'Muu' },
};

// Source type icons
const sourceTypeConfig = {
  email: { icon: EmailIcon, color: 'info', label: 'Sähköposti' },
  extension: { icon: ExtensionIcon, color: 'secondary', label: 'Selainlaajennus' },
  file_upload: { icon: UploadFileIcon, color: 'success', label: 'Tiedosto' },
  url_parse: { icon: LinkIcon, color: 'primary', label: 'URL' },
  csv_import: { icon: UploadFileIcon, color: 'warning', label: 'CSV Import' },
  ics_import: { icon: CalendarTodayIcon, color: 'info', label: 'ICS-tuonti' },
};

const ImportDetailDialog = ({ open, onClose, importData, t }) => {
  const navigate = useNavigate();
  const [fullData, setFullData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);

  useEffect(() => {
    if (open && importData?.id) {
      fetchFullData();
    } else {
      setFullData(null);
    }
  }, [open, importData?.id]);

  const fetchFullData = async () => {
    try {
      setLoading(true);
      const orgId = localStorage.getItem('currentOrganizationId');
      const response = await axios.get(`/api/imports/${importData.id}/?organization=${orgId}`);
      setFullData(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch import details:', err);
      setError(t('Failed to load details'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('fi-FI', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleDownload = () => {
    if (fullData?.file_url) {
      window.open(fullData.file_url, '_blank');
    }
  };

  const data = fullData || importData;
  if (!data) return null;

  const docConfig = documentTypeConfig[data.document_type] || documentTypeConfig.muu;
  const sourceConfig = sourceTypeConfig[data.source_type] || { icon: HelpOutlineIcon, color: 'default', label: data.source_type };
  const DocIcon = docConfig.icon;
  const SourceIcon = sourceConfig.icon;

  const getTitle = () => {
    if (data.original_filename) return data.original_filename;
    if (data.subject) return data.subject;
    if (data.source_title) return data.source_title;
    if (data.source_url) return data.source_url.substring(0, 50) + '...';
    return t('Import') + ' #' + data.id;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t('Import Details')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {getTitle()}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Type chips */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Chip
                icon={<SourceIcon fontSize="small" />}
                label={sourceConfig.label}
                color={sourceConfig.color}
              />
              {data.document_type && (
                <Chip
                  icon={<DocIcon fontSize="small" />}
                  label={docConfig.label}
                  color={docConfig.color}
                  variant="outlined"
                />
              )}
              {data.is_csv_import && data.entity_type && (
                <Chip
                  label={data.entity_type === 'customer' ? t('Customers') : t('Parts')}
                  variant="outlined"
                />
              )}
            </Box>

            {/* CSV Import Summary */}
            {data.is_csv_import && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('Import Summary')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">{t('Total rows')}</Typography>
                    <Typography variant="h6">{data.total_rows || 0}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">{t('Imported')}</Typography>
                    <Typography variant="h6" color="success.main">{data.imported_count || 0}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">{t('Skipped')}</Typography>
                    <Typography variant="h6" color="warning.main">{data.skipped_count || 0}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">{t('Errors')}</Typography>
                    <Typography variant="h6" color="error.main">{data.error_count || 0}</Typography>
                  </Grid>
                </Grid>
              </Paper>
            )}

            {/* CSV Import Skipped/Errors - Show prominently if any */}
            {data.is_csv_import && fullData?.import_details && (data.skipped_count > 0 || data.error_count > 0) && (
              <Paper variant="outlined" sx={{ p: 2, borderColor: 'warning.main' }}>
                <Typography variant="subtitle2" gutterBottom color="warning.main">
                  {t('Skipped and Errors')} ({(data.skipped_count || 0) + (data.error_count || 0)})
                </Typography>
                <Box sx={{ maxHeight: 250, overflow: 'auto' }}>
                  <List dense disablePadding>
                    {fullData.import_details
                      .filter(d => d.status === 'skipped' || d.status === 'error')
                      .map((detail, index) => (
                        <ListItem
                          key={index}
                          disableGutters
                          sx={{
                            py: 0.5,
                            bgcolor: detail.status === 'error' ? 'error.50' : 'warning.50',
                            borderRadius: 1,
                            mb: 0.5,
                            px: 1,
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            {detail.status === 'skipped' && (
                              <PendingIcon fontSize="small" color="warning" />
                            )}
                            {detail.status === 'error' && (
                              <CancelIcon fontSize="small" color="error" />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box>
                                <Typography variant="body2" color="text.secondary">
                                  {t('Row')} {detail.row}
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {detail.message}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                  </List>
                </Box>
              </Paper>
            )}

            {/* CSV Import Successful - Collapsible */}
            {data.is_csv_import && fullData?.import_details && data.imported_count > 0 && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom color="success.main">
                  {t('Successfully imported')} ({data.imported_count})
                </Typography>
                <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                  <List dense disablePadding>
                    {fullData.import_details
                      .filter(d => d.status === 'imported')
                      .slice(0, 30)
                      .map((detail, index) => (
                        <ListItem key={index} disableGutters sx={{ py: 0.25 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <CheckCircleIcon fontSize="small" color="success" />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="body2" color="text.secondary">
                                {t('Row')} {detail.row}: {detail.message}
                              </Typography>
                            }
                          />
                        </ListItem>
                      ))}
                    {fullData.import_details.filter(d => d.status === 'imported').length > 30 && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, pl: 3 }}>
                        ... {t('and')} {fullData.import_details.filter(d => d.status === 'imported').length - 30} {t('more rows')}
                      </Typography>
                    )}
                  </List>
                </Box>
              </Paper>
            )}

            {/* ICS Import Summary */}
            {data.is_ics_import && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('Import Summary')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">{t('Total events')}</Typography>
                    <Typography variant="h6">{data.total_events || 0}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">{t('Recognized')}</Typography>
                    <Typography variant="h6">{data.recognized_count || 0}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">{t('Imported')}</Typography>
                    <Typography variant="h6" color="success.main">{data.imported_count || 0}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">{t('Skipped')}</Typography>
                    <Typography variant="h6" color="text.secondary">{data.skipped_count || 0}</Typography>
                  </Grid>
                </Grid>
              </Paper>
            )}

            {/* ICS Import - Imported Maintenances */}
            {data.is_ics_import && fullData?.imported_maintenances && fullData.imported_maintenances.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BuildIcon fontSize="small" />
                  {t('Imported work orders')} ({fullData.imported_maintenances.length})
                </Typography>
                <List dense disablePadding>
                  {fullData.imported_maintenances.map((m) => (
                    <ListItemButton
                      key={m.id}
                      onClick={() => {
                        onClose();
                        navigate(`/maintenances/${m.id}`);
                      }}
                      sx={{
                        borderRadius: 1,
                        mx: -1,
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckCircleIcon fontSize="small" color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primary={m.vehicle || t('No vehicle')}
                        secondary={m.description || m.customer || '-'}
                      />
                      <ChevronRightIcon fontSize="small" color="action" />
                    </ListItemButton>
                  ))}
                </List>
              </Paper>
            )}

            {/* ICS Import - Errors */}
            {data.is_ics_import && fullData?.import_details && data.error_count > 0 && (
              <Paper variant="outlined" sx={{ p: 2, borderColor: 'error.main' }}>
                <Typography variant="subtitle2" gutterBottom color="error.main">
                  {t('Errors')} ({data.error_count})
                </Typography>
                <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                  <List dense disablePadding>
                    {fullData.import_details
                      .filter(d => d.status === 'error')
                      .map((detail, index) => (
                        <ListItem key={index} disableGutters sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <CancelIcon fontSize="small" color="error" />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="body2" color="error">
                                {detail.message}
                              </Typography>
                            }
                          />
                        </ListItem>
                      ))}
                  </List>
                </Box>
              </Paper>
            )}

            {/* Document preview / download */}
            {fullData?.file_url && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: 1.5, sm: 2 },
                  mb: data.file_content_type?.startsWith('image/') ? 2 : 0
                }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {t('Original Document')}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        wordBreak: 'break-word',
                      }}
                    >
                      {data.original_filename || t('Document')} ({formatFileSize(data.file_size_bytes)})
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownload}
                    sx={{ flexShrink: 0 }}
                  >
                    {t('Download')}
                  </Button>
                </Box>
                {/* Show image preview for screenshots */}
                {data.file_content_type?.startsWith('image/') && (
                  <Box
                    component="img"
                    src={fullData.file_url}
                    alt={data.original_filename || 'Screenshot'}
                    sx={{
                      width: '100%',
                      maxHeight: 400,
                      objectFit: 'contain',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  />
                )}
              </Paper>
            )}

            {/* Document metadata from LLM */}
            {fullData?.document_metadata && Object.keys(fullData.document_metadata).length > 0 && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('Document Metadata')}
                </Typography>
                <Grid container spacing={2}>
                  {fullData.document_metadata.document_number && (
                    <Grid item xs={6} sm={4}>
                      <Typography variant="caption" color="text.secondary">{t('Document number')}</Typography>
                      <Typography variant="body2">{fullData.document_metadata.document_number}</Typography>
                    </Grid>
                  )}
                  {fullData.document_metadata.vendor_name && (
                    <Grid item xs={6} sm={4}>
                      <Typography variant="caption" color="text.secondary">{t('Vendor')}</Typography>
                      <Typography variant="body2">{fullData.document_metadata.vendor_name}</Typography>
                    </Grid>
                  )}
                  {fullData.document_metadata.document_date && (
                    <Grid item xs={6} sm={4}>
                      <Typography variant="caption" color="text.secondary">{t('Document date')}</Typography>
                      <Typography variant="body2">{fullData.document_metadata.document_date}</Typography>
                    </Grid>
                  )}
                  {fullData.document_metadata.total_amount && (
                    <Grid item xs={6} sm={4}>
                      <Typography variant="caption" color="text.secondary">{t('Total')}</Typography>
                      <Typography variant="body2">
                        {fullData.document_metadata.total_amount} {fullData.document_metadata.currency || 'EUR'}
                      </Typography>
                    </Grid>
                  )}
                  {fullData.document_metadata.vendor_business_id && (
                    <Grid item xs={6} sm={4}>
                      <Typography variant="caption" color="text.secondary">{t('Business ID')}</Typography>
                      <Typography variant="body2">{fullData.document_metadata.vendor_business_id}</Typography>
                    </Grid>
                  )}
                  {fullData.document_metadata.reference_number && (
                    <Grid item xs={6} sm={4}>
                      <Typography variant="caption" color="text.secondary">{t('Reference')}</Typography>
                      <Typography variant="body2">{fullData.document_metadata.reference_number}</Typography>
                    </Grid>
                  )}
                  {fullData.document_metadata.items_summary && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">{t('Items')}</Typography>
                      <Typography variant="body2">{fullData.document_metadata.items_summary}</Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            )}

            {/* Source info */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('Source Information')}
              </Typography>
              <Grid container spacing={2}>
                {data.sender_email && (
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmailIcon fontSize="small" color="action" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">{t('Sender')}</Typography>
                        <Typography variant="body2">{data.sender_email}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}
                {data.source_type === 'email' && fullData?.body_html && (
                  <Grid item xs={12}>
                    <Button
                      variant="outlined"
                      startIcon={<VisibilityIcon />}
                      onClick={() => setEmailDialogOpen(true)}
                    >
                      {t('View email')}
                    </Button>
                  </Grid>
                )}
                {data.source_url && (
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <LinkIcon fontSize="small" color="action" sx={{ mt: 0.5 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">{t('Source URL')}</Typography>
                        <Link
                          href={data.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                        >
                          <Typography variant="body2" noWrap sx={{ maxWidth: 400 }}>
                            {data.source_url}
                          </Typography>
                          <OpenInNewIcon fontSize="small" />
                        </Link>
                      </Box>
                    </Box>
                  </Grid>
                )}
                {data.captured_by_name && (
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon fontSize="small" color="action" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">{t('Imported by')}</Typography>
                        <Typography variant="body2">{data.captured_by_name}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarTodayIcon fontSize="small" color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">{t('Import date')}</Typography>
                      <Typography variant="body2">{formatDate(data.created_at)}</Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {/* Parsed parts (suggestions) for email/extension imports */}
            {fullData?.suggestions && fullData.suggestions.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <InventoryIcon fontSize="small" />
                  {t('Parsed Parts')} ({fullData.suggestions.length})
                </Typography>
                <List dense disablePadding>
                  {fullData.suggestions.map((suggestion) => (
                    <ListItem
                      key={suggestion.id}
                      disableGutters
                      secondaryAction={
                        suggestion.status === 'imported' && suggestion.imported_part ? (
                          <Tooltip title={t('View in parts registry')}>
                            <IconButton
                              size="small"
                              onClick={() => {
                                onClose();
                                navigate(`/parts?highlight=${suggestion.imported_part}`);
                              }}
                            >
                              <ChevronRightIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null
                      }
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        {suggestion.status === 'pending' && (
                          <Tooltip title={t('Pending')}>
                            <PendingIcon fontSize="small" color="warning" />
                          </Tooltip>
                        )}
                        {suggestion.status === 'imported' && (
                          <Tooltip title={t('Imported')}>
                            <CheckCircleIcon fontSize="small" color="success" />
                          </Tooltip>
                        )}
                        {suggestion.status === 'declined' && (
                          <Tooltip title={t('Declined')}>
                            <CancelIcon fontSize="small" color="error" />
                          </Tooltip>
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={suggestion.name}
                        secondary={
                          <Box sx={{ display: 'flex', gap: 2 }}>
                            {(suggestion.primary_code || suggestion.manufacturer_code) && (
                              <Typography variant="caption">{suggestion.primary_code || suggestion.manufacturer_code}</Typography>
                            )}
                            {suggestion.price && (
                              <Typography variant="caption">{parseFloat(suggestion.price).toFixed(2)} €</Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}

            {/* Parts created (for file/URL imports that don't use suggestions) */}
            {fullData?.parts_created && fullData.parts_created.length > 0 && (!fullData?.suggestions || fullData.suggestions.length === 0) && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <InventoryIcon fontSize="small" />
                  {t('Parts Created')} ({fullData.parts_created.length})
                </Typography>
                <List dense disablePadding>
                  {fullData.parts_created.map((part) => (
                    <ListItemButton
                      key={part.id}
                      onClick={() => {
                        onClose();
                        navigate(`/parts?highlight=${part.id}`);
                      }}
                      sx={{
                        borderRadius: 1,
                        mx: -1,
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                    >
                      <ListItemText
                        primary={part.name}
                        secondary={
                          <Box sx={{ display: 'flex', gap: 2 }}>
                            {(part.primary_code || part.manufacturer_code) && (
                              <Typography variant="caption">{part.primary_code || part.manufacturer_code}</Typography>
                            )}
                            {part.price && (
                              <Typography variant="caption">{parseFloat(part.price).toFixed(2)} €</Typography>
                            )}
                          </Box>
                        }
                      />
                      <ChevronRightIcon fontSize="small" color="action" />
                    </ListItemButton>
                  ))}
                </List>
              </Paper>
            )}

            {/* Linked maintenances */}
            {fullData?.maintenances_linked && fullData.maintenances_linked.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BuildIcon fontSize="small" />
                  {t('Linked Maintenances')} ({fullData.maintenances_linked.length})
                </Typography>
                <List dense disablePadding>
                  {fullData.maintenances_linked.map((m) => (
                    <ListItemButton
                      key={m.id}
                      onClick={() => {
                        onClose();
                        navigate(`/maintenances/${m.id}`);
                      }}
                      sx={{
                        borderRadius: 1,
                        mx: -1,
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                    >
                      <ListItemText
                        primary={`#${m.work_number || m.id} - ${m.registration_number || '-'}`}
                        secondary={m.description}
                      />
                      <ChevronRightIcon fontSize="small" color="action" />
                    </ListItemButton>
                  ))}
                </List>
              </Paper>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          {t('Close')}
        </Button>
      </DialogActions>

      {/* Email HTML viewer dialog */}
      <Dialog
        open={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {data.subject || t('Email')}
            </Typography>
            <IconButton onClick={() => setEmailDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          {data.sender_email && (
            <Typography variant="body2" color="text.secondary">
              {data.sender_email}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <iframe
            srcDoc={fullData?.body_html || ''}
            sandbox="allow-popups allow-popups-to-escape-sandbox"
            title={data.subject || 'Email'}
            style={{
              width: '100%',
              minHeight: '500px',
              border: 'none',
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialogOpen(false)}>
            {t('Close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default withTranslation()(ImportDetailDialog);
